import { ConfigService } from '@akebono/core';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { LotTranslation, LotTypeEnum } from '../../graphql/service/graphql-auto-main-service';
import { AudioTranslationSpeedService } from './services/audio-translation-speed.service';
import {
  Lang,
  TranslationLangGroup,
  TranslationTypeEnum,
  TranslationTypePanel,
} from './types/lot-translations-collapse.types';

@Component({
  selector: 'app-lot-translations',
  templateUrl: './lot-translations.component.html',
  styleUrls: ['./lot-translations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotTranslationsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  set translations(value: LotTranslation[] | undefined) {
    this.translations$.next(value);
  }
  @Input() lotType: LotTypeEnum;
  @Input() isManager = false;
  @Input() isSharedLot = false;
  @Output() actionCompleted = new EventEmitter<void>();

  panels$: Observable<TranslationTypePanel[]>;

  private readonly translations$ = new ReplaySubject<LotTranslation[] | undefined>(1);
  LotTypeEnum = LotTypeEnum;
  TranslationTypeEnum = TranslationTypeEnum;

  private readonly destroy$ = new Subject<void>();

  @HostBinding('style.display') display: 'none' | 'block' = 'none';

  @ViewChildren('audio')
  audios!: QueryList<ElementRef<HTMLAudioElement>>;

  constructor(
    private configService: ConfigService,
    private audioTranslationSpeedService: AudioTranslationSpeedService,
    private translate: TranslateService,
  ) {}

  trackPanel = (_: number, panel: TranslationTypePanel) => panel.type;
  trackLangGroup = (_: number, group: TranslationLangGroup) => group.lang;
  trackTranslationItem = (_: number, translation: LotTranslation) => translation.id;

  ngOnInit(): void {
    this.panels$ = this.translations$.pipe(
      map((translations) =>
        (translations ?? []).filter((translation) => this.isRuOrEnLanguage(translation.language)),
      ),
      map((translations) =>
        this.isSharedLot
          ? translations.filter((translation) => !translation.aiModel)
          : translations,
      ),
      map((translations) => this.aggregateTypePanels(translations)),
      switchMap((panels) =>
        this.configService.getCurrentLanguage$().pipe(map(() => this.applyHeaders(panels))),
      ),
      tap((panels) => (this.display = panels.length ? 'block' : 'none')),
    );
  }

  private getType(translation: LotTranslation): TranslationTypeEnum {
    if (translation.aiModel) {
      return TranslationTypeEnum.AI;
    }

    if (translation.translatorId) {
      return TranslationTypeEnum.MANUAL;
    }

    return TranslationTypeEnum.AUTO;
  }

  private getLang(translation: LotTranslation): Lang {
    return ((translation.language ?? '').toLowerCase().startsWith('en') ? 'en' : 'ru') as Lang;
  }

  private isVoice(translation: LotTranslation): boolean {
    return !!translation.file;
  }

  private aggregateTypePanels(translations: LotTranslation[]): TranslationTypePanel[] {
    const order: TranslationTypeEnum[] = Object.values(TranslationTypeEnum);
    const byTranslationType = new Map<TranslationTypeEnum, TranslationTypePanel>();

    for (const translation of translations) {
      const type = this.getType(translation);
      const lang = this.getLang(translation);

      if (!byTranslationType.has(type)) {
        byTranslationType.set(type, {
          type,
          header: '',
          active: type === TranslationTypeEnum.MANUAL,
          langs: [],
        });
      }

      const panel = byTranslationType.get(type)!;

      let translationLangGroup = panel.langs.find((langGroup) => langGroup.lang === lang);

      if (!translationLangGroup) {
        translationLangGroup = { lang, text: [], voice: [] };
        panel.langs.push(translationLangGroup);
      }

      if (this.isVoice(translation)) {
        translationLangGroup.voice.push(translation);
      } else {
        translationLangGroup.text.push(translation);
      }
    }

    for (const panel of byTranslationType.values()) {
      panel.langs.sort((a, b) => (a.lang === 'ru' ? -1 : 1) - (b.lang === 'ru' ? -1 : 1));
    }

    return order
      .map((translationType) => byTranslationType.get(translationType))
      .filter((panel) => !!panel)
      .map((panel) => ({
        ...panel,
        langs: panel.langs.filter((langGroup) => langGroup.text.length || langGroup.voice.length),
      }))
      .filter((panel) => panel.langs.length > 0);
  }

  private applyHeaders(panels: TranslationTypePanel[]): TranslationTypePanel[] {
    for (const panel of panels) {
      panel.header = this.getTypeHeader(panel.type);
    }

    return panels;
  }

  private getTypeHeader(type: TranslationTypeEnum): string {
    switch (type) {
      case TranslationTypeEnum.AUTO:
        return this.translate.instant('USER_REQUESTS.TRANSLATION_PANELS.AUTO');
      case TranslationTypeEnum.AI:
        return this.translate.instant('USER_REQUESTS.TRANSLATION_PANELS.AI');
      case TranslationTypeEnum.MANUAL:
        return this.translate.instant('USER_REQUESTS.TRANSLATION_PANELS.MANUAL');
    }
  }

  ngAfterViewInit(): void {
    this.audioTranslationSpeedService.changes$.pipe(takeUntil(this.destroy$)).subscribe((speed) => {
      this.audios.forEach((audio) => (audio.nativeElement.playbackRate = speed));
    });

    const speed = this.audioTranslationSpeedService.speed;
    this.audios.forEach((audio) => (audio.nativeElement.playbackRate = speed));
  }

  private isRuOrEnLanguage(lang: string | null | undefined): lang is Lang {
    const normalizedLang = (lang ?? '').toLowerCase();
    return normalizedLang === 'ru' || normalizedLang === 'en';
  }

  onActionCompleted(): void {
    this.actionCompleted.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
